import { useState, useRef, useCallback } from "react";
import { Upload, FileText, Image, Video, X } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { validateFileSizeWithToast } from "../../../../lib/fileUploadValidation";

interface CitationFileDropzoneProps {
  onFileSelect: (file: File) => void;
  selectedFile: File | null;
  onClearFile: () => void;
  accept?: string;
}

const ACCEPTED_TYPES = {
  "application/pdf": [".pdf"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "image/webp": [".webp"],
  "video/mp4": [".mp4"],
  "video/quicktime": [".mov"],
};

const ACCEPTED_EXTENSIONS = Object.values(ACCEPTED_TYPES).flat();

function getFileTypeFromName(
  fileName: string,
): "pdf" | "image" | "video" | null {
  const ext = fileName.toLowerCase().split(".").pop();
  if (ext === "pdf") return "pdf";
  if (["jpg", "jpeg", "png", "webp"].includes(ext || "")) return "image";
  if (["mp4", "mov"].includes(ext || "")) return "video";
  return null;
}

function isValidFileType(file: File): boolean {
  const ext = `.${file.name.toLowerCase().split(".").pop()}`;
  return ACCEPTED_EXTENSIONS.includes(ext);
}

export const CitationFileDropzone = ({
  onFileSelect,
  selectedFile,
  onClearFile,
}: CitationFileDropzoneProps) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set drag over to false if we're leaving the dropzone entirely
    const relatedTarget = e.relatedTarget as Node | null;
    if (!e.currentTarget.contains(relatedTarget)) {
      setIsDragOver(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      setError(null);

      const files = e.dataTransfer.files;
      if (files.length === 0) return;

      const file = files[0];
      if (!isValidFileType(file)) {
        setError(
          "Please upload a PDF, image (JPG, PNG, WebP), or video (MP4, MOV) file",
        );
        return;
      }

      if (!validateFileSizeWithToast(file)) return;

      onFileSelect(file);
    },
    [onFileSelect],
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setError(null);
      const files = e.target.files;
      if (!files || files.length === 0) return;

      const file = files[0];
      if (!isValidFileType(file)) {
        setError(
          "Please upload a PDF, image (JPG, PNG, WebP), or video (MP4, MOV) file",
        );
        return;
      }

      if (!validateFileSizeWithToast(file)) return;

      onFileSelect(file);
    },
    [onFileSelect],
  );

  const handleClick = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const fileType = selectedFile ? getFileTypeFromName(selectedFile.name) : null;

  if (selectedFile) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex-1 flex flex-col items-center justify-center p-6 bg-stone-50 border border-stone-200 rounded-lg">
          <div className="w-16 h-16 rounded-full bg-stone-100 flex items-center justify-center mb-4">
            {fileType === "pdf" ? (
              <FileText className="w-8 h-8 text-[#ef3608]" />
            ) : fileType === "video" ? (
              <Video className="w-8 h-8 text-[#ef3608]" />
            ) : (
              <Image className="w-8 h-8 text-[#ef3608]" />
            )}
          </div>
          <p className="text-sm font-medium text-stone-900 text-center truncate max-w-full px-4">
            {selectedFile.name}
          </p>
          <p className="text-xs text-stone-500 mt-1">
            {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
          </p>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearFile}
            className="mt-4 text-stone-600 hover:text-stone-900"
          >
            <X className="w-4 h-4 mr-1" />
            Choose Different File
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div
        className={`
          flex-1 flex flex-col items-center justify-center p-8
          border-2 border-dashed cursor-pointer
          transition-all duration-200 ease-out
          ${
            isDragOver
              ? "border-[#ef3608] bg-[#ef3608]/5 scale-[1.01]"
              : "border-stone-300 bg-stone-50 hover:border-stone-400 hover:bg-stone-100"
          }
        `}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept={ACCEPTED_EXTENSIONS.join(",")}
          onChange={handleFileInput}
        />

        <div
          className={`
            w-16 h-16 rounded-full flex items-center justify-center mb-4
            transition-all duration-200
            ${isDragOver ? "bg-[#ef3608]/10" : "bg-stone-200"}
          `}
        >
          <Upload
            className={`
              w-7 h-7 transition-all duration-200
              ${isDragOver ? "text-[#ef3608] scale-110" : "text-stone-500"}
            `}
          />
        </div>

        <p className="text-sm font-medium text-stone-700 text-center">
          {isDragOver ? "Drop file here" : "Drag and drop a file"}
        </p>
        <p className="text-xs text-stone-500 mt-1">or click to browse</p>

        <div className="flex items-center gap-3 mt-4">
          <div className="flex items-center gap-1.5 text-xs text-stone-400">
            <FileText className="w-3.5 h-3.5" />
            <span>PDF</span>
          </div>
          <div className="w-px h-3 bg-stone-300" />
          <div className="flex items-center gap-1.5 text-xs text-stone-400">
            <Image className="w-3.5 h-3.5" />
            <span>JPG, PNG, WebP</span>
          </div>
          <div className="w-px h-3 bg-stone-300" />
          <div className="flex items-center gap-1.5 text-xs text-stone-400">
            <Video className="w-3.5 h-3.5" />
            <span>MP4, MOV</span>
          </div>
        </div>

        {error && (
          <p className="text-xs text-red-600 mt-3 text-center">{error}</p>
        )}
      </div>
    </div>
  );
};

export default CitationFileDropzone;
